<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";


// Services
import VehicleService from '@/api/services/vehicle.service';
import VehicleRequestService from '@/api/services/vehicleRequest.service';

// Icons Components
import IconVehicle from '@/components/icons/IconVehicle.vue';
import HeaderTitle from "@/components/layout/HeaderTitle.vue";

// Dialog Components
import VehicleRequestDialog from '@/components/shared/Vehicles/VehicleRequestDialog.vue';

const route = useRoute();
const authStore = useAuthStore();
const uiStore = useUiStore();

const user = computed(() => {
    return authStore.getUserLogged;
})

const vehicleId = computed(() => {
    if (route.params.id) {
        return route.params.id;
    }
    return null;
})

const isNewVehicle = computed(() => {
    if (vehicleId.value) return false
    return true;
})

const vehicle = ref({
    totalSeats: 1,
    maxSeats: 1,
    active: true,
});

const request = ref([]);
const isLoading = ref(false);

const dialogVehicleRequest = ref(false);
const selectedRequest = ref();

const fuelTypeOptions = ref([
    { label: 'Diesel', value: 'diesel' },
    { label: 'Gasolina', value: 'gasoline' },
    { label: 'Eléctrico', value: 'electric' },
    { label: 'Híbrido', value: 'hybrid' },
    { label: 'Híbrido enchufable', value: 'plugin_hybrid' },
]);

const adBlueRefuelCount = computed(() => {
    if (vehicle.value.requiresAdBlue) {
        return `${vehicle.value.refuelCountSinceAdBlue}/4 repostajes`
    }
    return null;
})

const adBlueStatus = computed(() => {

    if (vehicle.value.requiresAdBlue && vehicle.value.needsAdBlue) {
        return "urgent";     // rojo/amarillo
    }
    if (vehicle.value.requiresAdBlue && vehicle.value.refuelCountSinceAdBlue === 3) {
        return "warning";    // naranja
    }
    if (vehicle.value.requiresAdBlue && vehicle.value.refuelCountSinceAdBlue < 3) {
        return "ok";         // verde
    }
    return "none";         // no requiere
})

onMounted(() => {
    if (!isNewVehicle.value) {
        getVehicle();
        getVehicleRequests();
    }
})

// Getters
const getVehicle = () => {
    uiStore.startLoading();
    VehicleService.getVehicle(vehicleId.value)
        .then((res) => {
            vehicle.value = res.data
            uiStore.stopLoading();
        })
        .catch(() => {
            Notification.addNotification('Error al cargar el vehículo seleccionado.', 'error', 6000);
            uiStore.stopLoading();
        })
}

const getVehicleRequests = () => {
    uiStore.startLoading();
    let params = {
        'vehicle.id': vehicleId.value,
        'order[startDate]': "ASC",
    }
    VehicleRequestService.getVehicleRequests(params)
        .then((res) => {
            request.value = res.data['hydra:member']
            uiStore.stopLoading();
        })
        .catch(() => {
            Notification.addNotification('Error al cargar el vehículo seleccionado.', 'error', 6000);
            uiStore.stopLoading();
        })
}

// Formats Table
const formatDate = (dateString) => {
    if (!dateString) return ''

    const date = new Date(dateString)

    const datePart = new Intl.DateTimeFormat('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }).format(date)

    const timePart = new Intl.DateTimeFormat('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).format(date)


    // Capitalizar el mes
    const capitalizedDate = datePart.replace(
        /de (\p{L}+)/u,
        (_, month) => `de ${month.charAt(0).toUpperCase()}${month.slice(1)}`
    )

    return `${capitalizedDate} ${timePart}`
}

const getStatus = (status) => {
    switch (status) {
        case 'pending':
            return 'Pendiente';
        case 'approved':
            return 'Aprobado';
        case 'rejected':
            return 'Rechazado';
        case 'cancelled':
            return 'Cancelado';
        case 'closed':
            return 'Cerrado';
        default:
            return '';
    }
}

const changeSeats = () => {
    if(!vehicle.value.maxSeats){
        vehicle.value.maxSeats = vehicle.value.totalSeats;
    }
    if(vehicle.value.maxSeats && vehicle.value.maxSeats < vehicle.value.totalSeats ){
        vehicle.value.maxSeats = vehicle.value.totalSeats;
    }
}

// Dialog Requests

const closeDialogRequestForm = () => {
    dialogVehicleRequest.value = false;
    selectedRequest.value = null;
}
const openDialogRequestForm = (session) => {
    selectedRequest.value = session;
    dialogVehicleRequest.value = true;
}

const resetVehicle = () => {
    getVehicle();
    closeDialogRequestForm();
}

const canEditRequest = (v) => {
    if (user.value.id === v.employee?.id) {
        if (v.status && v.status === 'pending') {
            return true;
        }
    }
    return false;
}

// Buttons
const createVehicle = async () => {
    if (!vehicle.value.licensePlate) {
        notification.addNotification({
            title: 'Error',
            message: 'El campo Matrícula no debe estar vacío.',
            type: 'error'
        });
        return;
    } else if (!vehicle.value.active) {
        notification.addNotification({
            title: 'Error',
            message: 'El campo Estado no debe ser vacío.',
            type: 'error'
        });
        return;

    } else if (!vehicle.value.brand) {
        notification.addNotification({
            title: 'Error',
            message: 'El campo Marca no debe ser vacío.',
            type: 'error'
        });
        return;
    } else if (!vehicle.value.model) {
        notification.addNotification({
            title: 'Error',
            message: 'El campo Marca no debe ser vacío.',
            type: 'error'
        });
        return;
    }

    await uiStore.stopLoading();
    isLoading.value = true;
    if (!isNewVehicle.value) {
        VehicleService.updateVehicle(vehicle.value.id, vehicle.value)
            .then(() => {
                notification.addNotification({
                    title: 'Coche actualizado',
                    message: 'El coche ha sido actualizado correctamente',
                    type: 'success'
                });
                goBack();
            }).catch((error) => {
                notification.addNotification({
                    title: 'Error',
                    message: 'Error al actualizar el coche',
                    type: 'error'
                });
                console.log("Error:", error);
            }).finally(async () => {
                await uiStore.stopLoading();
            }
            );
    } else {
        VehicleService.createVehicle(vehicle.value)
            .then(() => {
                notification.addNotification({
                    title: 'Coche creado',
                    message: 'El Coche ha sido creado correctamente',
                    type: 'success'
                });
                goBack();
            }).catch((error) => {
                notification.addNotification({
                    title: 'Error',
                    message: 'Error al crear el coche',
                    type: 'error'
                });
                console.log("Error:", error);
            }).finally(async () => {
                await uiStore.stopLoading();
                isLoading.value = false;
            });
    }

}

const goBack = () => {
    history.back();
}

const registerAdBlue = () => {
    if (!vehicle.value.requiresAdBlue || !vehicle.value.needsAdBlue) {
        return;
    }
    VehicleService.registerAdBlue(vehicleId.value)
        .then((res) => {
            if (res && res.status && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    title: 'success',
                    message: 'AdBlue añadido al coche correctamente',
                    type: 'success'
                });
                resetVehicle();
            }
        }).catch((error) => {
            notification.addNotification({
                title: 'Error',
                message: 'Error al crear el coche',
                type: 'error'
            });
            console.log("Error:", error);
        })

}

</script>

<template>
    <div>
        <header-title title="Vehículos" />
        <div class="d-flex flex-row justify-content-start">
            <IconVehicle class="iconVehicle" />
            <div class="title-text ml-4">
                {{ isNewVehicle ? 'Nuevo Vehículo' : 'Vehículo' }}
            </div>
        </div>
        <!-- Vehicle Form -->
        <div class="row d-flex">
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="licensePlate">Matrícula:</label>
                        <el-input v-model="vehicle.licensePlate" id="licensePlate"
                            placeholder="Inserte la matrícula"></el-input>
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="alias">Alias:</label>
                        <el-input v-model="vehicle.alias" id="alias" placeholder="Inserte el alias"></el-input>
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="active">Estado:</label>
                        <el-switch v-model="vehicle.active" id="active" active-text="Activo" inactive-text="Inactivo"
                            style="margin-left: 10px; margin-top: 2px;" />
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="brand">Marca:</label>
                        <el-input v-model="vehicle.brand" id="brand" placeholder="Inserte la marca"></el-input>
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="model">Modelo:</label>
                        <el-input v-model="vehicle.model" id="model" placeholder="Inserte el modelo"></el-input>
                    </div>
                </div>
            </div>
            <div class="col-md-2 el-col-xs-12">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="seats">Asientos:</label>
                        <el-input-number v-model="vehicle.totalSeats" :min="1" :step="1" id="seats"
                            @change="changeSeats"
                            placeholder="Número de asientos" />
                    </div>
                </div>
            </div>
            <div class="col-md-2 el-col-xs-12" v-if="vehicle && vehicle.totalSeats">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="seats">Asientos máximos:</label>
                        <el-input-number v-model="vehicle.maxSeats" :min="vehicle.totalSeats" :step="1" id="seats"
                            placeholder="Max asientos" />
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="currentKm">Kilómetros actuales:</label>
                        <el-input-number v-model="vehicle.currentKm" :min="1" :step="0.01" id="currentKm" class="w-100"
                            placeholder="Kilómetros actuales" />
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="fuelType">Tipo de combustible:</label>
                        <el-select v-model="vehicle.fuelType" placeholder="Tipo de combustible" filterable clearable
                            id="fuelType">
                            <el-option v-for="u in fuelTypeOptions" :key="u.value" :label="u.label" :value="u.value">
                            </el-option>
                        </el-select>
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24" v-if="vehicle.fuelType === 'diesel'">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item mt-1">
                        <label class="w-100" for="requiresAdBlue">¿Necesita Adblue?</label>
                        <el-switch v-model="vehicle.requiresAdBlue" id="requiresAdBlue" active-text="Sí"
                            inactive-text="No" style="margin-left: 10px; margin-top: 2px;" />
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24" v-if="vehicle.requiresAdBlue && !isNewVehicle">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="refuelCountSinceAdBlue">Repostajes desde el último AdBlue:</label>
                        <el-input-number v-model="vehicle.refuelCountSinceAdBlue" :min="0" :step="0.01"
                            id="refuelCountSinceAdBlue" class="w-100" disabled
                            placeholder="Repostajes desde el último AdBlue" />
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24" v-if="vehicle.requiresAdBlue && !isNewVehicle">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="lastAdBlueDate">Fecha del último AdBlue:</label>
                        <el-date-picker v-model="vehicle.lastAdBlueDate" type="datetime"
                            placeholder="Fecha del último repostaje de AdBlue" id="lastAdBlueDate"
                            format="DD/MM/YYYY HH:mm" :clearable="false" class="mb-1 w-100" disabled />
                    </div>
                </div>
            </div>
            <div class="col-md-4 el-col-xs-24" v-if="vehicle.requiresAdBlue && !isNewVehicle">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item" v-if="adBlueStatus !== 'none'">
                        <label class="w-100">Repostajes AdBlue:</label>
                        <div>
                            <el-button v-if="adBlueStatus === 'urgent'"
                                :style="{ backgroundColor: '#ff3030', color: '#fff' }" @click="registerAdBlue">
                                <el-icon style="margin-right: 5px;">
                                    <WarningFilled />
                                </el-icon> {{ adBlueRefuelCount }} – Registrar AdBlue
                            </el-button>
                            <el-button v-else-if="adBlueStatus === 'warning'"
                                :style="{ backgroundColor: '#e6a23c', color: '#fff' }" disabled>
                                <el-icon style="margin-right: 5px;">
                                    <WarnTriangleFilled />
                                </el-icon> Próximo repostaje necesitará AdBlue
                            </el-button>
                            <el-button v-else-if="adBlueStatus === 'ok'"
                                :style="{ backgroundColor: '#67c23a', color: '#fff' }" disabled>
                                <el-icon style="margin-right: 5px;"><Select /></el-icon> {{ adBlueRefuelCount }}
                            </el-button>

                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-12 el-col-xs-24">
                <div class="d-flex flex-column flex-nowrap mt-4 w-100 label-common">
                    <div class="form-item">
                        <label class="w-100" for="notes">Notas:</label>
                        <textarea v-model="vehicle.notes" class="w-100 p-3 font18" rows="5" id="notes" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Table vehicle request -->
        <div v-if="!isNewVehicle && request && request.length > 0" class="table-not-mobile">
            <div class="table">
                <div class="title-text ml-4" style="text-align: left;">
                    Solicitudes del vehículo:
                </div>
                <div>
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha inicio</th>
                                <th>Fecha fin</th>
                                <th>Empleado</th>
                                <th>Kms</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="re in request" :key="re.id">
                                <td class="pointer" @click="openDialogRequestForm(re)">
                                    {{ formatDate(re.startDate) }}
                                </td>

                                <td class="pointer" @click="openDialogRequestForm(re)">
                                    {{ formatDate(re.endDate) }}
                                </td>
                                <td class="pointer" @click="openDialogRequestForm(re)">
                                    {{ re.employee.fullName }}
                                </td>
                                <td class="pointer" @click="openDialogRequestForm(re)">
                                    {{ re.traveledKm }}
                                </td>
                                <td class="pointer" @click="openDialogRequestForm(re)">
                                    {{ getStatus(re.status) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div v-if="!isNewVehicle && request && request.length > 0" class="table-mobile mb-4 mt-2">
            <div class="title-text ml-4 vehicle-mobile-section-title">
                Solicitudes del vehículo:
            </div>
            <div v-for="re in request" :key="re.id">
                <el-row class="mt-3">
                    <el-card class="card-vehicle-request-detail">
                        <div class="vehicle-request-detail-card pointer" @click="openDialogRequestForm(re)">
                            <div class="vehicle-request-detail-header">
                                <div class="el-col-xs-24 vehicle-request-detail-first-row">
                                    <span class="vehicle-item-label">Fecha inicio: </span>{{ formatDate(re.startDate) }}
                                </div>
                                <div class="buttons d-inline-flex justify-content-end vehicle-request-detail-actions">
                                    <div class="pointer">
                                        <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Editar"
                                            @click.stop="openDialogRequestForm(re)">
                                            <font-awesome-icon icon="circle" style="color: #f7d20e" />
                                            <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                                        </font-awesome-layers>
                                    </div>
                                </div>
                            </div>
                            <div class="el-col-xs-24">
                                <span class="vehicle-item-label">Fecha fin: </span>{{ formatDate(re.endDate) }}
                            </div>
                            <div class="el-col-xs-24">
                                <span class="vehicle-item-label">Empleado: </span>{{ re.employee.fullName }}
                            </div>
                            <div class="el-col-xs-24">
                                <span class="vehicle-item-label">Kms: </span>{{ re.traveledKm }}
                            </div>
                            <div class="el-col-xs-24">
                                <span class="vehicle-item-label">Estado: </span>{{ getStatus(re.status) }}
                            </div>
                        </div>
                    </el-card>
                </el-row>
            </div>
        </div>
        <div v-if="!isNewVehicle && request && request.length <= 0">
            No hay solicitudes para este vehículo
        </div>
        <div class="d-flex justify-content-end vehicle-item-actions">
            <button class="btn btn_yellow btn-search " @click="goBack()">
                VOLVER
            </button>
            <button class="btn btn_red btn-search ml-4" @click="createVehicle()" :disabled="isLoading">
                {{ isNewVehicle ? 'CREAR' : 'GUARDAR' }}
            </button>
        </div>
        <el-dialog v-model="dialogVehicleRequest" width="600" class="vehicle-item-dialog" :show-close="false" :lock-scroll="true"
            @close="closeDialogRequestForm">
            <!-- Mi material // Datos material -->
            <VehicleRequestDialog v-if="dialogVehicleRequest" :canEdit="canEditRequest(selectedRequest)"
                @finish="resetVehicle()" @close="closeDialogRequestForm" :vehicleRequest="selectedRequest" />
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
.iconVehicle {
    width: 50px;
    fill: $bg-blue;
}

::v-deep(.el-input-number__increase) {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
}

::v-deep(.el-input-number__decrease) {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
}

:deep(.el-button.is-disabled) {
    cursor: default !important;
    opacity: 1;
}


.btn_red {
    color: white !important;
}

.table {
    padding-top: 10px !important;
}

.table-mobile {
    display: none;
}

.vehicle-mobile-section-title {
    text-align: left;
}

.card-vehicle-request-detail {
    border-radius: 18px;
}

.vehicle-request-detail-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 8px;
    padding-bottom: 10px;
}

.vehicle-request-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
}

.vehicle-request-detail-first-row {
    flex: 1;
    min-width: 0;
}

.vehicle-item-label {
    color: $bg-blue;
    font-weight: 700;
}

.vehicle-request-detail-actions {
    flex-shrink: 0;
}

@media (max-width: 767px) {
    .table-not-mobile {
        display: none;
    }

    .table-mobile {
        display: block;
    }

    .vehicle-item-actions {
        flex-direction: column;
        gap: 12px;
    }

    .vehicle-item-actions .btn-search {
        width: 100%;
        margin-left: 0 !important;
    }

    :deep(.vehicle-item-dialog .el-dialog) {
        width: calc(100vw - 24px) !important;
        max-width: 600px;
        margin: 12px auto;
    }
}

@media (min-width: 768px) {
    .table-mobile {
        display: none;
    }
}
</style>
